import type { Punto, Bbox, Anillo, MultiPoligono } from './geometria';
import { bboxDePuntos, puntoEnAnillo } from './geometria';

/**
 * Distancias planas entre segmentos y un indice de rejilla para medir la distancia de cada
 * parcela al curso de agua mas cercano (criterio "cerca de un rio" de la busqueda). Sin
 * dependencias: se usa en el worker y en los tests.
 */

export function distanciaPuntoSegmento(p: Punto, a: Punto, b: Punto): number {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  let t = 0;
  if (l2 > 0) t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2));
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

function orientacion(a: Punto, b: Punto, c: Punto): number {
  return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}

/** Cruce estricto de dos segmentos (los casos de contacto o colinealidad los resuelve la distancia 0). */
export function segmentosSeCruzan(a1: Punto, a2: Punto, b1: Punto, b2: Punto): boolean {
  const d1 = orientacion(b1, b2, a1), d2 = orientacion(b1, b2, a2);
  const d3 = orientacion(a1, a2, b1), d4 = orientacion(a1, a2, b2);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

export function distanciaSegmentos(a1: Punto, a2: Punto, b1: Punto, b2: Punto): number {
  if (segmentosSeCruzan(a1, a2, b1, b2)) return 0;
  return Math.min(
    distanciaPuntoSegmento(a1, b1, b2), distanciaPuntoSegmento(a2, b1, b2),
    distanciaPuntoSegmento(b1, a1, a2), distanciaPuntoSegmento(b2, a1, a2));
}

export interface SegmentoCercano {
  dist: number;
  id: string;
}

/**
 * Indice de rejilla de segmentos de polilinea. Cada segmento se apunta en las celdas que cubre
 * su caja; una consulta recorre solo las celdas de la caja pedida. Con celdas de 250 m y los
 * rios de una zona de 25 km2 (unos 20.000 segmentos) medir 50.000 parcelas tarda segundos.
 */
export class IndiceSegmentos {
  private ax: number[] = [];
  private ay: number[] = [];
  private bx: number[] = [];
  private by: number[] = [];
  private ids: string[] = [];
  private celdas = new Map<string, number[]>();
  private visto: number[] = [];
  private sello = 0;

  constructor(readonly celda = 250) {}

  get tamano(): number {
    return this.ids.length;
  }

  anadir(id: string, linea: Punto[]): void {
    for (let i = 1; i < linea.length; i++) {
      const [x1, y1] = linea[i - 1], [x2, y2] = linea[i];
      const k = this.ids.length;
      this.ax.push(x1); this.ay.push(y1); this.bx.push(x2); this.by.push(y2); this.ids.push(id); this.visto.push(0);
      const i0 = Math.floor(Math.min(x1, x2) / this.celda), i1 = Math.floor(Math.max(x1, x2) / this.celda);
      const j0 = Math.floor(Math.min(y1, y2) / this.celda), j1 = Math.floor(Math.max(y1, y2) / this.celda);
      for (let i2 = i0; i2 <= i1; i2++) {
        for (let j = j0; j <= j1; j++) {
          const clave = `${i2},${j}`;
          const lista = this.celdas.get(clave);
          if (lista) lista.push(k); else this.celdas.set(clave, [k]);
        }
      }
    }
  }

  /** Indices de los segmentos apuntados en las celdas que toca la caja, sin repetir. */
  candidatos(caja: Bbox): number[] {
    const sello = ++this.sello;
    const salida: number[] = [];
    const i0 = Math.floor(caja[0] / this.celda), i1 = Math.floor(caja[2] / this.celda);
    const j0 = Math.floor(caja[1] / this.celda), j1 = Math.floor(caja[3] / this.celda);
    for (let i = i0; i <= i1; i++) {
      for (let j = j0; j <= j1; j++) {
        const lista = this.celdas.get(`${i},${j}`);
        if (!lista) continue;
        for (const k of lista) {
          if (this.visto[k] === sello) continue;
          this.visto[k] = sello;
          salida.push(k);
        }
      }
    }
    return salida;
  }

  /**
   * Segmento mas cercano al anillo a menos de `maxDist` (o null). Un rio que atraviesa la parcela
   * o discurre por su interior da distancia 0.
   */
  distanciaAnillo(anillo: Anillo, maxDist: number): SegmentoCercano | null {
    const n = anillo.length;
    if (n < 2) return null;
    const caja = bboxDePuntos(anillo);
    const cand = this.candidatos([caja[0] - maxDist, caja[1] - maxDist, caja[2] + maxDist, caja[3] + maxDist]);
    let mejor = maxDist;
    let mejorId: string | null = null;
    for (const k of cand) {
      const sminx = Math.min(this.ax[k], this.bx[k]), smaxx = Math.max(this.ax[k], this.bx[k]);
      const sminy = Math.min(this.ay[k], this.by[k]), smaxy = Math.max(this.ay[k], this.by[k]);
      // Cota inferior barata: distancia entre las cajas.
      const gx = Math.max(caja[0] - smaxx, sminx - caja[2], 0), gy = Math.max(caja[1] - smaxy, sminy - caja[3], 0);
      if (Math.hypot(gx, gy) >= mejor) continue;
      const a: Punto = [this.ax[k], this.ay[k]], b: Punto = [this.bx[k], this.by[k]];
      let d = Infinity;
      for (let i = 0; i < n; i++) {
        const dd = distanciaSegmentos(anillo[i], anillo[(i + 1) % n], a, b);
        if (dd < d) { d = dd; if (d === 0) break; }
      }
      if (d > 0 && puntoEnAnillo(a, anillo)) d = 0;
      if (d < mejor) { mejor = d; mejorId = this.ids[k]; }
    }
    return mejorId === null ? null : { dist: mejor, id: mejorId };
  }

  distanciaMultiPoligono(mp: MultiPoligono, maxDist: number): SegmentoCercano | null {
    let mejor: SegmentoCercano | null = null;
    for (const pol of mp) {
      if (pol.length === 0) continue;
      const r = this.distanciaAnillo(pol[0], mejor ? mejor.dist : maxDist);
      if (r && (!mejor || r.dist < mejor.dist)) mejor = r;
    }
    return mejor;
  }
}
